import { LangLink as Link } from "@/components/LangLink";
import { Reveal } from "@/components/Reveal";
import { useTranslation } from "react-i18next";

type SplitSectionProps = {
  image?: string;
  video?: string;
  eyebrow?: string;
  title: string;
  description: string;
  href: string;
  tone: "amber" | "blue" | "warm";
  inverted?: boolean;
};

export function SplitSection({
  image,
  video,
  eyebrow,
  title,
  description,
  href,
  tone,
  inverted = false,
}: SplitSectionProps) {
  const { t } = useTranslation("common");
  const overlayColors =
    tone === "amber"
      ? "from-amber-950/70 via-black/40 to-black/70"
      : tone === "blue"
        ? "from-[#12149B]/80 via-black/50 to-black/80"
        : "from-stone-900/60 via-black/40 to-black/70";

  const gradientDir = inverted ? "bg-gradient-to-l" : "bg-gradient-to-r";

  return (
    <section className="relative min-h-[90vh] flex items-center overflow-hidden border-t border-border group/split">
      {video ? (
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          ref={(el) => {
            if (el) {
              el.muted = true;
              el.play().catch((err) => {
                console.warn("Video play failed:", err);
              });
            }
          }}
          className="absolute inset-0 w-full h-full object-cover scale-105 group-hover/split:scale-100 transition-transform duration-[2000ms]"
          poster={image}
        >
          <source src={video} type="video/mp4" />
        </video>
      ) : (
        image && (
          <img
            src={image}
            alt={title}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover scale-105 group-hover/split:scale-100 transition-transform duration-[2000ms]"
            width={1920}
            height={900}
          />
        )
      )}

      <div className={`absolute inset-0 ${gradientDir} ${overlayColors}`} />

      <div className="relative container-bizu grid lg:grid-cols-2 gap-10 items-center py-24">
        <Reveal
          effect={inverted ? "right" : "left"}
          className={inverted ? "order-1 lg:order-2 lg:ml-auto text-right" : "order-1"}
        >
          {eyebrow && <span className="section-tag">{eyebrow}</span>}

          <h2 className={`display text-4xl md:text-6xl max-w-xl ${eyebrow ? "mt-4" : "mt-0"}`}>
            {title}
          </h2>
        </Reveal>

        <Reveal
          effect={inverted ? "left" : "right"}
          delay={150}
          className={`max-w-md flex flex-col ${inverted ? "order-2 lg:order-1 items-end lg:items-start text-right lg:text-left ml-auto lg:ml-0" : "order-2 lg:ml-auto items-start text-left"}`}
        >
          <p className="text-white/85 leading-relaxed">{description}</p>

          <Link to={href as any} className="btn-pill mt-8">
            {t("learnMore", "Learn more")}
            <span className="sr-only"> about {title}</span>
          </Link>
        </Reveal>
      </div>
    </section>
  );
}
